import axios from "axios";
import { tokenStore } from "./tokenStore";

// Single shared boundary for exchanging the httpOnly refresh cookie for a new
// access token. The refresh token is single-use and rotates on every call —
// presenting an already-rotated token is treated as theft and revokes every
// session for the user (see orchestrator's auth routes). Every 401 handler in
// the app (request.ts, AuthContext.tsx, stream.ts) must go through this one
// function so concurrent 401s collapse into a single /auth/refresh call
// instead of racing each other into a forced logout.

const getApiBaseUrl = (): string | undefined => {
  const envURL = process.env.REACT_APP_BASE_URL;
  if (envURL && envURL.trim().length) return envURL;
  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:1245`;
  }
  return undefined;
};

let inFlight: Promise<string | null> | null = null;

const doRefresh = async (): Promise<string | null> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/api/v1/auth/refresh`,
      undefined,
      { withCredentials: true }
    );
    const accessToken = response.data?.access_token ?? null;
    tokenStore.set(accessToken);
    return accessToken;
  } catch {
    tokenStore.clear();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth:refresh-failed"));
    }
    return null;
  }
};

export const refreshAccessToken = (): Promise<string | null> => {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};
